import api from "@/lib/axios";

/** Strip React Query-only fields before sending to API */
const toApiPayload = (filters) => {
  if (!filters) return {};
  const { queryKey, ...payload } = filters;
  return payload;
};

/**
 * Large customFilters in GET query strings exceed URL length limits (~8KB)
 * and cause net::ERR_CONNECTION_CLOSED / HTTP2 errors around ~150+ values.
 */
const countCustomFilterValues = (customFilters = {}) =>
  Object.values(customFilters).reduce((count, value) => {
    if (Array.isArray(value)) return count + value.length;
    if (value !== undefined && value !== null && value !== "") return count + 1;
    return count;
  }, 0);

const shouldUsePostForFilters = (payload) => {
  const customFilters = payload?.customFilters || {};
  if (countCustomFilterValues(customFilters) >= 80) return true;
  try {
    return JSON.stringify(payload).length > 4000;
  } catch {
    return false;
  }
};

export const applicationService = {

  // Small filters: GET (existing). Large filters: POST body to avoid URL limits.
  getApplicationsByVacancyId: async (vacancyId, filters = null) => {
    try {
      const payload = toApiPayload(filters);
      if (shouldUsePostForFilters(payload)) {
        const response = await api.post(
          `/vacancy/${vacancyId}/applications/search`,
          payload,
        );
        return response.data;
      }
      const response = await api.get(`/vacancy/${vacancyId}/applications`, {
        params: payload,
      });
      return response.data;
    } catch (error) {
      throw new Error("Failed to fetch applications by vacancy id: " + error.message);
    }
  },


  getApplicationsByVacancyIdKanban: async (vacancyId, filters = null) => {
    try {
      const payload = toApiPayload(filters);
      if (shouldUsePostForFilters(payload)) {
        const response = await api.post(
          `/vacancy/${vacancyId}/applications/kanban/search`,
          payload,
        );
        return response.data;
      }
      const response = await api.get(`/vacancy/${vacancyId}/applications/kanban`, {
        params: payload,
      });
      return response.data;
    } catch (error) {
      throw new Error("Failed to fetch applications by vacancy id: " + error.message);
    } 
  },

  getApplicationLocks: async (vacancyId) => {
    try {
      const response = await api.get(`/vacancy/${vacancyId}/applications/locks`, {
        params: { _t: Date.now() },
      });
      return response.data;
    } catch (error) {
      throw new Error("Failed to fetch application locks: " + error.message);
    }
  },

  getApplicationsForPreScreen: async (vacancyId, filters = null) => {
    try {
      const { application_id, queryKey, ...customFilters } = filters || {};

      const params = {};

      if (Object.keys(customFilters).length > 0) {
        params.customFilters = customFilters;
      }

      if (application_id) {
        params.application_id = Array.isArray(application_id)
          ? application_id
          : [application_id];
      }

      if (shouldUsePostForFilters(params)) {
        const response = await api.post(
          `/vacancy/${vacancyId}/applications/pre-screen/search`,
          params,
        );
        return response.data;
      }

      const response = await api.get(`/vacancy/${vacancyId}/applications/pre-screen`, {
        params,
      });
      return response.data;
    } catch (error) {
      throw new Error("Failed to fetch applications by vacancy id: " + error.message);
    }
  },

  //update application status
  //   applications/bulk-update-status
  //array of ids and status
  bulkUpdateApplicationStatus: async (application_ids, vacancy_status_id, params = {}) => {
    try {
      const response = await api.post(`/application/bulk-update-status`, { application_ids, vacancy_status_id, ...params });
      return response.data;
    } catch (error) {
      throw new Error("Failed to bulk update application status: " + error.message);
    }
  },


  //update applications status
  updateApplicationsStatus: async (payload) => {
    try {
      const response = await api.put(`/applications/statuses`, payload);
      return response.data;
    } catch (error) {
      throw new Error("Failed to update applications status: " + error.message);
    }
  },

  TagsUpload: async (applicationId, tagNames) => {
    try {
      const response = await api.post(`/applications/tags`, {
        application_id: applicationId,
        tag_names: tagNames
      });
      return response.data;
    } catch (error) {
      throw new Error("Failed to Upload Tags: " + error.message);
    }
  },
  BulkScoreCardsUpdate: async (scoreCards) => {
    try {
      const response = await api.post(`/applications/bluk-add-score`, {
        ...scoreCards
      });
      return response.data;
    } catch (error) {
      throw new Error("Failed to update score cards: " + error.message);
    }
  },
  // application by id

  getApplictionByID: async (application_id) => {
    try {
      const response = await api.get(`/applications/${application_id}`);
      return response.data;
    } catch (error) {
      throw new Error("Failed to fetch application by ID: " + error.message);
    }
  },

  BulkTagsUpdate: async (tags) => {
    try {
      const response = await api.post(`/applications/bluk-add-tags`, {
        ...tags
      });
      return response.data;
    } catch (error) {
      throw new Error("Failed to update tags: " + error.message);
    }
  },


  BulkNotesUpdate: async (notes) => {
    try {
      const response = await api.post(`/applications/bluk-add-notes`, {
        ...notes
      });
      return response.data;
    } catch (error) {
      throw new Error("Failed to update notes: " + error.message);
    }
  },
  AllTags: async () => {
    try {
      const response = await api.get('/application/tags');
      return response;
    } catch (error) {
      throw new Error("Failed to fetch Tags " + error.message);
    }
  },

  // Hold/Resume application
  toggleApplicationHold: async (payload) => {
    try {
      const response = await api.put(`/applications/hold`, payload);
      return response.data;
    } catch (error) {
      throw new Error("Failed to update application hold status: " + error.message);
    }
  },
  getApplicationsByVacancyIdStats: async (vacancyId, filters = null) => {
    try {
      const payload = toApiPayload(filters);
      const response = await api.get(`/vacancy/${vacancyId}/applications/stats`, {
        params: payload,
      });
      return response.data;
    } catch (error) {
      throw new Error("Failed to fetch applications by vacancy id stats: " + error.message);
    }
  },

  // Generate summary for selected candidates or filtered applications (same payload as download CVs)
  generateSummary: async (ids, vacancyId = null, filters = null) => {
    try {
      let payload = {};
      if (!ids || ids.length === 0) {
        if (vacancyId) {
          payload = { vacancy_id: vacancyId };
        } else {
          throw new Error("Either application IDs or vacancy ID must be provided");
        }
      } else {
        payload = { application_ids: ids };
        if (vacancyId) {
          payload.vacancy_id = vacancyId;
        }
      }
      if (filters && Object.keys(filters).length > 0) {
        payload = { ...payload, filters };
      }
      const response = await api.post("/download-cvs-with-summary-sync", payload);
      return response.data;
    } catch (error) {
      throw new Error("Failed to generate summary: " + error.message);
    }
  },
};

